import { setNotification } from "features/Notification/notificationSlice";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Tracking Info's Bulk Update Status (user, 2026-10-19), as on Manufacturing
 * -> DHL Tracking but for every courier with a tracking API (DHL, UPS).
 *
 * Every row shipped in the last 30 days that is not delivered and not voided
 * gets its latest status from its courier. The work is asked for in chunks
 * (POST /ups-tracking/bulk-update-status, `nextAfterId`): no request runs
 * long enough to time out, progress shows as it goes, and Stop ends it after
 * the chunk in flight. The API paces its courier calls; when UPS still says
 * "too many requests" twice, it answers rateLimited and the run ends there.
 */
// customerId (optional, a per-company number): only that customer's rows
// -- the Customer Details "Tracking" tab.
// url (optional): the endpoint answering the same way -- Manufacturing ->
// PK Shipments' /pk-shipments/update-tracking (user, 2026-10-30).
export default function BulkUpdateStatus({
  lastUpdated,
  onDone,
  customerId,
  url = "/ups-tracking/bulk-update-status",
}) {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(null); // { done, total } while running
  const stopRef = useRef(false);

  const run = async () => {
    stopRef.current = false;
    const totals = { updated: 0, failed: 0, skipped: 0 };
    let firstError = null;
    let afterId = null;
    let total = 0;
    let done = 0;
    let stopped = false;
    let rateLimited = false;
    setProgress({ done: 0, total: 0 });
    try {
      do {
        const { data } = await API.post(
          url,
          { afterId, ...(customerId ? { customerId } : {}) },
          { headers: authHeader() },
        );
        if (!data?.success) throw new Error(data?.message || "Bulk update failed");
        if (data.total !== undefined) total = data.total;
        done += data.processed || 0;
        totals.updated += data.updated || 0;
        totals.failed += data.failed || 0;
        totals.skipped += data.skipped || 0;
        if (!firstError && data.errors?.length) firstError = data.errors[0];
        afterId = data.nextAfterId;
        if (data.rateLimited) rateLimited = true;
        setProgress({ done, total });
        if (stopRef.current && afterId) stopped = true;
      } while (afterId && !stopped);

      const counts = `Updated ${totals.updated}, failed ${totals.failed}, skipped ${totals.skipped}`;
      const example = firstError
        ? ` (e.g. ${firstError.trackingNumber}: ${firstError.message})`
        : "";
      dispatch(
        setNotification(
          rateLimited
            ? {
                message: `Stopped: the courier's rate limit was reached -- try again in a few minutes. ${counts}`,
                type: "error",
              }
            : {
                message: `${stopped ? "Stopped. " : ""}${counts}${example}`,
                type: totals.failed && !totals.updated ? "error" : "success",
              },
        ),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setProgress(null);
      if (onDone) onDone();
    }
  };

  const running = !!progress;

  return (
    <div className="d-flex align-items-center gap-2">
      {lastUpdated && !running && (
        <small className="text-muted text-nowrap" data-testid="last-updated">
          Last Updated: {new Date(lastUpdated).toLocaleString()}
        </small>
      )}
      <button
        type="button"
        className="btn btn-info text-nowrap"
        onClick={run}
        disabled={running}
        title="Get the latest status of every shipment from the last 30 days that is not delivered or voided"
      >
        <i className={`mdi ${running ? "mdi-loading mdi-spin" : "mdi-refresh"}`}></i>{" "}
        {running
          ? `Updating ${progress.done} / ${progress.total || "…"}`
          : "Bulk Update Status"}
      </button>
      {running && (
        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={() => (stopRef.current = true)}
        >
          Stop
        </button>
      )}
    </div>
  );
}
